import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RelationshipStatusSubmission } from '../../../../../models/submissions/relationship-status-submission';
import { BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import * as moment from 'moment/moment';
import { RelationshipStatusUtils } from '../../../../../utils/relationship-status-utils';
import { RelationshipStatusSubmissionDetailsUtils } from '../../../../../utils/relationship-status-submission-details-utils';

@Component({
  selector: 'app-spouse-personal-info-changes',
  templateUrl: './spouse-personal-info-changes.component.html',
  styleUrls: ['./spouse-personal-info-changes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpousePersonalInfoChangesComponent implements OnChanges {
  @Input() submission!: RelationshipStatusSubmission;

  private _submission = new BehaviorSubject<RelationshipStatusSubmission | null>(null);
  public submission$ = this._submission.pipe(filter((sub): sub is RelationshipStatusSubmission => sub !== null));

  public submissionInfoItems$ = this.submission$.pipe(
    map(sub => this.createSubmissionInfoItemArrays(sub)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public previousInfo$ = this.submissionInfoItems$.pipe(map(info => info.previous));
  public newInfo$ = this.submissionInfoItems$.pipe(map(info => info.new));

  public previousInfoDisplayString$ = this.submission$.pipe(
    map(sub => {
      if (!sub.previous || !RelationshipStatusUtils.isPartneredStatus(sub.previous.relationshipStatusId)) {
        return $localize`Not Applicable`;
      }
      return '';
    })
  );

  public newInfoDisplayString$ = this.submission$.pipe(
    map(sub => {
      if (!RelationshipStatusUtils.isPartneredStatus(sub.new.relationshipStatusId)) {
        return $localize`Not Applicable`;
      }
      if (
        !RelationshipStatusSubmissionDetailsUtils.spousePersonalInfoChanged(
          sub?.previous?.spouse ?? null,
          sub.new.spouse
        )
      ) {
        return $localize`No Changes`;
      }
      return '';
    })
  );

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.submission) {
      this._submission.next(this.submission);
    }
  }

  private createSubmissionInfoItemArrays(submission: RelationshipStatusSubmission) {
    const previousData = submission.previous;
    const newData = submission.new;

    const spouseExistenceChanged = !!previousData?.spouse !== !!newData?.spouse;

    const items = [
      {
        label: $localize`First Name`,
        prev: previousData?.spouse?.firstName ?? '',
        new: newData?.spouse?.firstName ?? ''
      },
      {
        label: $localize`Middle Name`,
        prev: previousData?.spouse?.middleName ?? '',
        new: newData?.spouse?.middleName ?? ''
      },
      {
        label: $localize`Last Name`,
        prev: previousData?.spouse?.lastName ?? '',
        new: newData?.spouse?.lastName ?? ''
      },
      {
        label: $localize`Date of Birth`,
        prev: previousData?.spouse?.birthDate ? moment(previousData.spouse.birthDate).format('MMM D, YYYY') : '',
        new: newData?.spouse?.birthDate ? moment(newData.spouse.birthDate).format('MMM D, YYYY') : ''
      },
      {
        label: $localize`Social Insurance Number`,
        prev: previousData?.spouse?.formattedSIN ?? '',
        new: newData?.spouse?.formattedSIN ?? ''
      }
    ];

    return {
      previous: items.map(item => ({
        label: item.label,
        value: item.prev || '',
        hasChanged: item.prev !== item.new || spouseExistenceChanged
      })),
      new: items.map(item => ({
        label: item.label,
        value: item.new || '',
        hasChanged: item.prev !== item.new || spouseExistenceChanged
      }))
    };
  }
}
